import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Users, Clock, Home, Layers, DollarSign, TrendingUp, Building2, Package as PackageIcon, UserPlus, Share2, Link2, Mail, BarChart3, CheckCircle2, Sparkles, ArrowRight, Copy, QrCode, PlayCircle } from 'lucide-react';
export function DashboardPendingPage() {
  const navigate = useNavigate();
  const {
    builderId
  } = useParams<{
    builderId: string;
  }>();
  const bid = builderId || 'mattamy';
  // Setup summary — what they've configured
  const setupSummary = [{
    icon: Building2,
    label: 'Communities',
    value: 5,
    color: 'bg-blue-50 text-blue-600',
    path: `/${bid}/communities`
  }, {
    icon: Home,
    label: 'Home Models',
    value: 12,
    color: 'bg-emerald-50 text-emerald-600',
    path: `/${bid}/specs`
  }, {
    icon: Layers,
    label: 'Options',
    value: 87,
    color: 'bg-purple-50 text-purple-600',
    path: `/${bid}/options`
  }, {
    icon: PackageIcon,
    label: 'Packages',
    value: 4,
    color: 'bg-amber-50 text-amber-600',
    path: `/${bid}/packages`
  }];
  // Placeholder for the bar chart — show models as a list with "0 buyers"
  const placeholderModels = ['The Lawson', 'The Samson', 'The Pine', 'The Cedar', 'The Birch'];
  return <div className="space-y-8 max-w-7xl mx-auto" data-id="element-4942">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-4943">
        <div data-id="element-4944">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-4945">Dashboard</h1>
          <p className="text-slate-500 mt-1" data-id="element-4946">
            Your setup is complete — now let's get buyers in the door.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-4947">
          <Button variant="outline" onClick={() => navigate(`/${bid}/visualizer-preview`)} leftIcon={<PlayCircle className="h-4 w-4" data-id="element-4949" />} data-id="element-4948">
            Preview Buyer View
          </Button>
          <Button onClick={() => navigate(`/${bid}/customers`)} leftIcon={<UserPlus className="h-4 w-4" data-id="element-4951" />} data-id="element-4950">
            Invite First Buyer
          </Button>
        </div>
      </div>

      {/* Setup Complete Banner */}
      <Card className="bg-gradient-to-br from-emerald-50 via-white to-primary-50 border-emerald-200" data-id="element-4952">
        <CardContent className="p-6" data-id="element-4953">
          <div className="flex items-start gap-4" data-id="element-4954">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-4955">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" data-id="element-4956" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-4957">
              <div className="flex items-center gap-2 mb-1" data-id="element-4958">
                <h2 className="text-lg font-semibold text-slate-900" data-id="element-4959">
                  You're all set up!
                </h2>
                <Badge variant="success" className="text-[11px]" data-id="element-4960">
                  Ready
                </Badge>
              </div>
              <p className="text-sm text-slate-600 mb-4" data-id="element-4961">
                Your communities, models, options, and packages are configured.
                Once buyers start exploring, you'll see real-time analytics
                here.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-4962">
                {setupSummary.map(item => {
                const Icon = item.icon;
                return <button key={item.label} onClick={() => navigate(item.path)} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all text-left" data-id="element-4963">
                      <div className={`w-9 h-9 rounded-lg ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-4964">
                        <Icon className="h-4 w-4" data-id="element-4965" />
                      </div>
                      <div className="min-w-0" data-id="element-4966">
                        <p className="text-xs text-slate-500" data-id="element-4967">{item.label}</p>
                        <p className="text-lg font-bold text-slate-900 leading-tight" data-id="element-4968">
                          {item.value}
                        </p>
                      </div>
                    </button>;
              })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards — empty state */}
      <div data-id="element-4969">
        <h2 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider" data-id="element-4970">
          Buyer Engagement
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4971">
          {[{
          icon: Users,
          label: 'Active Buyers',
          iconBg: 'bg-slate-100',
          iconColor: 'text-slate-400'
        }, {
          icon: DollarSign,
          label: 'Upgrade Revenue',
          iconBg: 'bg-slate-100',
          iconColor: 'text-slate-400'
        }, {
          icon: TrendingUp,
          label: 'Avg Upgrade Value',
          iconBg: 'bg-slate-100',
          iconColor: 'text-slate-400'
        }, {
          icon: Clock,
          label: 'Total Time Spent',
          iconBg: 'bg-slate-100',
          iconColor: 'text-slate-400'
        }].map(item => {
          const Icon = item.icon;
          return <Card key={item.label} className="border-dashed" data-id="element-4972">
                <CardContent className="p-6" data-id="element-4973">
                  <div className="flex items-center gap-4" data-id="element-4974">
                    <div className={`p-3 ${item.iconBg} rounded-lg flex-shrink-0`} data-id="element-4975">
                      <Icon className={`h-6 w-6 ${item.iconColor}`} data-id="element-4976" />
                    </div>
                    <div className="min-w-0" data-id="element-4977">
                      <p className="text-sm font-medium text-slate-500" data-id="element-4978">
                        {item.label}
                      </p>
                      <h3 className="text-2xl font-bold text-slate-300" data-id="element-4979">—</h3>
                      <p className="text-xs text-slate-400 mt-1" data-id="element-4980">
                        No buyers yet
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>

      {/* Two-column: Get buyers in + Empty chart preview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4981">
        {/* Get buyers section */}
        <Card className="lg:col-span-2" data-id="element-4982">
          <CardHeader data-id="element-4983">
            <CardTitle data-id="element-4984">Get your first buyers</CardTitle>
            <p className="text-sm text-slate-500 mt-1" data-id="element-4985">
              Share your VR experience with prospects to start collecting data
            </p>
          </CardHeader>
          <CardContent className="space-y-3" data-id="element-4986">
            <button onClick={() => navigate(`/${bid}/customers`)} className="w-full flex items-center gap-4 p-4 rounded-lg border border-slate-200 hover:border-primary-300 hover:bg-primary-50/30 transition-all text-left group" data-id="element-4987">
              <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0" data-id="element-4988">
                <Mail className="h-5 w-5" data-id="element-4989" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-4990">
                <h3 className="font-semibold text-slate-900 text-sm" data-id="element-4991">
                  Invite buyers by email
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-4992">
                  Send a personalized invite with their VR tour link
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-all" data-id="element-4993" />
            </button>

            <button onClick={() => navigate(`/${bid}/communities`)} className="w-full flex items-center gap-4 p-4 rounded-lg border border-slate-200 hover:border-primary-300 hover:bg-primary-50/30 transition-all text-left group" data-id="element-4994">
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0" data-id="element-4995">
                <Link2 className="h-5 w-5" data-id="element-4996" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-4997">
                <h3 className="font-semibold text-slate-900 text-sm" data-id="element-4998">
                  Share a public community link
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-4999">
                  Embed on your website or share with marketing campaigns
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-all" data-id="element-5000" />
            </button>

            <button onClick={() => navigate(`/${bid}/communities`)} className="w-full flex items-center gap-4 p-4 rounded-lg border border-slate-200 hover:border-primary-300 hover:bg-primary-50/30 transition-all text-left group" data-id="element-5001">
              <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0" data-id="element-5002">
                <QrCode className="h-5 w-5" data-id="element-5003" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-5004">
                <h3 className="font-semibold text-slate-900 text-sm" data-id="element-5005">
                  Generate QR codes for sales centers
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-5006">
                  Print or display in showrooms for instant access
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-all" data-id="element-5007" />
            </button>

            <button onClick={() => navigate(`/${bid}/visualizer-preview`)} className="w-full flex items-center gap-4 p-4 rounded-lg border border-slate-200 hover:border-primary-300 hover:bg-primary-50/30 transition-all text-left group" data-id="element-5008">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-5009">
                <PlayCircle className="h-5 w-5" data-id="element-5010" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-5011">
                <h3 className="font-semibold text-slate-900 text-sm" data-id="element-5012">
                  Preview what buyers see
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-5013">
                  Walk through the VR experience yourself before sharing
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-all" data-id="element-5014" />
            </button>
          </CardContent>
        </Card>

        {/* What's coming */}
        <div className="space-y-6" data-id="element-5015">
          <Card data-id="element-5016">
            <CardHeader data-id="element-5017">
              <CardTitle data-id="element-5018">What you'll see soon</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3" data-id="element-5019">
              <div className="flex items-start gap-3" data-id="element-5020">
                <div className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-5021">
                  <BarChart3 className="h-4 w-4" data-id="element-5022" />
                </div>
                <div className="min-w-0" data-id="element-5023">
                  <p className="text-sm font-medium text-slate-900" data-id="element-5024">
                    Buyer distribution
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-5025">
                    See which models are most popular
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-5026">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-5027">
                  <Sparkles className="h-4 w-4" data-id="element-5028" />
                </div>
                <div className="min-w-0" data-id="element-5029">
                  <p className="text-sm font-medium text-slate-900" data-id="element-5030">
                    Top selected options
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-5031">
                    Track upgrade trends in real time
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-5032">
                <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-5033">
                  <TrendingUp className="h-4 w-4" data-id="element-5034" />
                </div>
                <div className="min-w-0" data-id="element-5035">
                  <p className="text-sm font-medium text-slate-900" data-id="element-5036">
                    Revenue insights
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-5037">
                    Upgrade revenue per buyer & community
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-5038">
                <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-5039">
                  <Clock className="h-4 w-4" data-id="element-5040" />
                </div>
                <div className="min-w-0" data-id="element-5041">
                  <p className="text-sm font-medium text-slate-900" data-id="element-5042">
                    Engagement metrics
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-5043">
                    Time spent, sessions, drop-off
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 text-white border-slate-800" data-id="element-5044">
            <CardContent className="p-6" data-id="element-5045">
              <Share2 className="h-7 w-7 mb-3 text-primary-400" data-id="element-5046" />
              <h3 className="font-bold text-base mb-2" data-id="element-5047">Ready to launch?</h3>
              <p className="text-slate-400 text-sm mb-4" data-id="element-5048">
                Our team can help you put together a launch plan and get the
                most out of your setup.
              </p>
              <Button variant="secondary" size="sm" className="w-full !bg-white !text-slate-900 hover:!bg-slate-100" onClick={() => navigate(`/${bid}/support`)} data-id="element-5049">
                Get launch help
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Empty Chart Preview */}
      <Card data-id="element-5050">
        <CardHeader data-id="element-5051">
          <div className="flex items-center justify-between" data-id="element-5052">
            <CardTitle data-id="element-5053">Buyer Distribution by Model</CardTitle>
            <Badge variant="neutral" className="text-[11px] bg-slate-100 text-slate-500 border-slate-200" data-id="element-5054">
              Waiting for data
            </Badge>
          </div>
        </CardHeader>
        <CardContent data-id="element-5055">
          <div className="space-y-3 py-2" data-id="element-5056">
            {placeholderModels.map(name => <div key={name} className="flex items-center gap-4" data-id="element-5057">
                <div className="w-24 text-sm text-slate-500 flex-shrink-0" data-id="element-5058">
                  {name}
                </div>
                <div className="flex-1 h-8 rounded bg-slate-50 border border-slate-100 border-dashed flex items-center px-3" data-id="element-5059">
                  <span className="text-xs text-slate-400" data-id="element-5060">0 buyers</span>
                </div>
              </div>)}
          </div>
          <div className="text-center pt-6 mt-2 border-t border-slate-100" data-id="element-5061">
            <p className="text-sm text-slate-500" data-id="element-5062">
              This chart will populate as buyers begin exploring your models.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>;
}